// Cloud profiles use the household member UUID; anton/anya remain legacy local
// identifiers until the owner explicitly maps historical health data.
export type Profile = string;
export type Owner = Profile | 'shared';

export interface Medicine {
  id: string;
  name: string;
  activeIngredient?: string;
  dosage?: string;
  form?: string;
  category?: string;
  usageSummary?: string;
  usageTags?: string[];
  // How the dosage form is physically taken (swallow whole/chew/dissolve/let
  // dissolve under the tongue) -- a fact about the manufactured form, never a
  // dosing schedule (frequency/max dose/timing vs food) -- see docs/AI_RULES.md.
  intakeMethod?: string;
  // Dosing schedule (frequency/max dose/food timing) read verbatim from a
  // specific found official page -- never from general knowledge. Always
  // set together: dosingScheduleSourceUrl lets the user verify the quote
  // themselves. See docs/AI_RULES.md ("Medicine dosing schedule").
  dosingScheduleText?: string;
  dosingScheduleSource?: string;
  dosingScheduleSourceUrl?: string;
  owner?: Owner;
  amount?: number | string;
  unit?: string;
  threshold?: number | string;
  expiry?: string;
  storage?: string;
  note?: string;
  photo?: string;
}

export type Country = 'russia' | 'israel' | 'other';

export interface LabResult {
  id: string;
  owner: Profile;
  // Cloud-only normalized link. `owner` remains the compatibility and local
  // identifier, while this UUID points at the real medical profile.
  medicalProfileId?: string;
  // Exact label copied from the laboratory report. Never translate or normalize.
  analyte: string;
  originalName?: string;
  // Russian translation or plain-language expansion shown below the original label.
  russianName?: string;
  // Full international medical name; shown only inside the detail card.
  internationalName?: string;
  // Stable technical identifier used for matching within one country.
  canonicalCode?: string;
  // Search-only synonyms and common abbreviations.
  aliases?: string[];
  country: Country;
  labName?: string;
  // Exactly one of value/valueText is set — qualitative results (e.g. urine
  // dipstick "NEGATIVE"/"NORMAL") have no number to plot.
  value?: number;
  valueText?: string;
  unit?: string;
  refLow?: number;
  refHigh?: number;
  refText?: string;
  // Report section this analyte was printed under (e.g. "Химия крови",
  // "Гематология") — used to group the Показатели list.
  panel?: string;
  takenOn: string;
  // Cloud mode: all readings extracted from one PDF/photo share documentId,
  // so the source file is stored once in the private Storage bucket.
  documentId?: string;
  // Immutable locator of an AI suggestion in its original source. These are
  // assigned from the physical page and printed top-to-bottom row order, not
  // from an analyte name or a medical value. Manual entries deliberately have
  // no locator and are never automatically rejected as duplicates.
  sourcePage?: number;
  sourceRow?: number;
  sourceSection?: string;
  // Compatibility for older cloud rows and local mode. New cloud uploads use
  // documentId and do not duplicate this base64 data across readings.
  sourceDocument?: string;
  notes?: string;
}

export interface LocalData {
  activeUser: Profile;
  medicines: Medicine[];
  labResults: LabResult[];
  settings: { theme?: 'auto' | 'light' | 'dark' };
}

const STORAGE_KEY = 'medai.data.v1';
const defaults: LocalData = { activeUser: 'anton', medicines: [], labResults: [], settings: { theme: 'auto' } };

function normalize(raw: unknown): LocalData {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const medicines = Array.isArray(value.medicines) ? value.medicines : Array.isArray(value.meds) ? value.meds : [];
  // "metrics" was the pre-v0.4 placeholder key — the app never had a form to
  // populate it, so there's no real data to migrate from it.
  const labResults = Array.isArray(value.labResults) ? value.labResults : [];
  const activeUser = typeof value.activeUser === 'string' && value.activeUser ? value.activeUser : 'anton';
  const settings = value.settings && typeof value.settings === 'object' ? value.settings as LocalData['settings'] : { theme: 'auto' as const };
  return { activeUser, medicines: medicines as Medicine[], labResults: labResults as LabResult[], settings };
}

export function loadData(): LocalData {
  try {
    return normalize(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return defaults;
  }
}

export function saveData(data: LocalData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// Whether the user chose "продолжить локально" (skip cloud sign-in) — kept
// separate from LocalData so it survives even if that gets reset/imported.
// Without persisting this, refreshing the page always bounced back to the
// sign-in screen even though the local data itself was intact, which looked
// like data loss.
const LOCAL_MODE_KEY = 'medai.localMode.v1';

export function loadLocalModePreference(): boolean {
  return localStorage.getItem(LOCAL_MODE_KEY) === '1';
}

export function saveLocalModePreference(active: boolean): void {
  if (active) localStorage.setItem(LOCAL_MODE_KEY, '1');
  else localStorage.removeItem(LOCAL_MODE_KEY);
}

/**
 * Which family profile the app should show after the cloud household loads.
 *
 * `activeUser` lives in `localStorage`, so it exists per origin: a new device,
 * a new browser or a new domain starts from the legacy default 'anton'. That
 * value matches no cloud row once historical data has been mapped to real
 * member UUIDs, so every screen filters down to nothing and a full household
 * looks empty with no explanation. Keep a stored choice that is still a member
 * of this household; otherwise show the signed-in person, whose own data they
 * are most likely looking for.
 */
export function resolveActiveProfile(stored: Profile, memberIds: string[], signedInUserId: string): Profile {
  // No household yet, or local mode: there is nothing to reconcile against.
  if (!memberIds.length) return stored;
  if (memberIds.includes(stored)) return stored;
  if (memberIds.includes(signedInUserId)) return signedInUserId;
  return memberIds[0];
}

export function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
